import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Clock } from 'lucide-react';
import { Button } from '../../../components/Shared/Button';
import { Input } from '../../../components/Shared/Input';
import { FormField } from '../../../components/Shared/FormField';
import { FacilityFlowCoreModulesPlanningEntitiesDriverRole as DriverRoleEnum } from '../../../api/generated/models';
import type { DriverRole } from '../../../store/useTimeclockStore';
import type { VehicleDto } from '../../../store/useVehicleStore';

/** Vehicle and role planned for the day (FACF-118); both null = nothing planned. */
export interface PlannedVehicleChoice {
  vehicleId: string | null;
  driverRole: DriverRole | null;
}

interface WorkDayDialogProps {
  isOpen: boolean;
  onClose: () => void;
  date: string;
  employeeName: string;
  onSave: (startTime: string, endTime: string, targetHours: number, vehicle: PlannedVehicleChoice) => void;
  initialStartTime?: string;
  initialEndTime?: string;
  initialTargetHours?: number;
  /** Active vehicles of the tenant; the vehicle section is hidden without any. */
  vehicles?: VehicleDto[];
  initialVehicle?: PlannedVehicleChoice;
  isEditMode?: boolean;
}

export function WorkDayDialog({ isOpen, onClose, date, employeeName, onSave, initialStartTime, initialEndTime, initialTargetHours, vehicles = [], initialVehicle, isEditMode }: WorkDayDialogProps) {
  const { t, i18n } = useTranslation(['planning', 'common', 'vehicles']);
  const [startTime, setStartTime] = useState('07:00');
  const [endTime, setEndTime] = useState('15:00');
  const [targetHours, setTargetHours] = useState(8);
  const [vehicleId, setVehicleId] = useState('');
  const [driverRole, setDriverRole] = useState<DriverRole>(DriverRoleEnum.NUMBER_1);

  // Re-fill when dialog opens with new values
  const [prevOpen, setPrevOpen] = useState(false);
  if (isOpen && !prevOpen) {
    setStartTime(initialStartTime || '07:00');
    setEndTime(initialEndTime || '15:00');
    setTargetHours(initialTargetHours ?? 8);
    setVehicleId(initialVehicle?.vehicleId ?? '');
    setDriverRole(initialVehicle?.driverRole === DriverRoleEnum.NUMBER_2 ? DriverRoleEnum.NUMBER_2 : DriverRoleEnum.NUMBER_1);
  }
  if (isOpen !== prevOpen) setPrevOpen(isOpen);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(startTime, endTime, targetHours, {
      vehicleId: vehicleId || null,
      driverRole: vehicleId ? driverRole : null,
    });
    onClose();
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(i18n.language, {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Clock className="w-5 h-5" />
              {isEditMode ? t('planning:workDay.titleEdit') : t('planning:workDay.titleCreate')}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {employeeName} · {formatDate(date)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label={t('common:actions.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Time Range */}
          <div className="grid grid-cols-2 gap-4">
            <FormField label={t('planning:fields.startTime')} required>
              <Input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </FormField>
            <FormField label={t('planning:fields.endTime')} required>
              <Input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </FormField>
          </div>

          {/* Target Hours */}
          <FormField label={t('planning:fields.targetHours')} required>
            <Input
              type="number"
              min="0"
              max="24"
              step="0.5"
              value={targetHours}
              onChange={(e) => setTargetHours(parseFloat(e.target.value))}
            />
          </FormField>

          {vehicles.length > 0 && (
            <div className="grid grid-cols-2 gap-4">
              <FormField label={t('planning:workDay.vehicle')}>
                <select
                  value={vehicleId}
                  onChange={(e) => setVehicleId(e.target.value)}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-[13px] text-slate-700 bg-white"
                >
                  <option value="">{t('planning:workDay.noVehicle')}</option>
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.licensePlate}
                    </option>
                  ))}
                </select>
              </FormField>
              {vehicleId && (
                <FormField label={t('planning:workDay.driverRole')}>
                  <select
                    value={driverRole}
                    onChange={(e) => setDriverRole(Number(e.target.value) as DriverRole)}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-[13px] text-slate-700 bg-white"
                  >
                    <option value={DriverRoleEnum.NUMBER_1}>{t('vehicles:driverRole.driver')}</option>
                    <option value={DriverRoleEnum.NUMBER_2}>{t('vehicles:driverRole.coDriver')}</option>
                  </select>
                </FormField>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={handleSave}>
            {isEditMode ? t('common:actions.save') : t('planning:workDay.actionCreate')}
          </Button>
        </div>
      </div>
    </div>
  );
}
